import { useEffect, useRef } from 'react';
import { useCart } from './StoreContext';
import { useAccount } from './AccountContext';
import { syncCart } from '../utils/accountApi';
import { latestRequest } from '../utils/latestRequest';
import { log } from '../core/log';
import type { CartSyncRequest } from '../core/types/requests';
type CartSyncLine = CartSyncRequest['lines'][number];

/** Na rust pas versturen: snel +/− tikken wordt één verzoek. */
const DELAY_MS = 2000;

/**
 * Bewaart de winkelmand op de server (PUT /account/cart) voor de mail "verlaten winkelmand".
 * Alleen voor een ingelogde klant met nieuwsbrieftoestemming; anders gaat er niets heen. Een lege
 * mand gaat ook mee, zodat er na een bestelling geen herinnering komt. Geen melding bij een fout:
 * de mand zelf werkt gewoon door, dit is alleen een geheugensteun voor de server.
 * Staat apart (niet in AccountContext), zodat het account niet opnieuw tekent bij elke wijziging in de mand.
 *
 * Volgorde: elk verzoek krijgt een volgnummer; een antwoord van een ouder verzoek wordt genegeerd,
 * zodat "verstuurd" nooit naar een oudere mand wijst.
 *
 * Toestemming uit: meteen stoppen. Een wachtende timer wordt geannuleerd (opruimen van het effect)
 * en er gaat geen nieuw verzoek meer heen. Een verzoek dat al onderweg is kan de browser niet
 * terughalen; daarom controleert de server bij ontvangst zelf opnieuw of de klant nog toestemming
 * heeft, en bewaart hij zonder toestemming niets (en wist hij een eerder bewaarde mand).
 */
export function CartServerSync() {
  const {
    lines
  } = useCart();
  const {
    account
  } = useAccount();
  const allowed = Boolean(account?.customer.prefs.newsletter);
  const sent = useRef<string | null>(null);
  const requests = useRef(latestRequest());

  // De inhoud als tekst: een nieuwe array met dezelfde regels start geen nieuw verzoek.
  const key = JSON.stringify(lines.map((l): CartSyncLine => ({
    productId: l.product.id,
    qty: l.qty
  })));
  useEffect(() => {
    const guard = requests.current;
    if (!allowed) {
      // Een antwoord dat nog onderweg is, telt niet meer.
      guard.invalidate();
      sent.current = null;
      return;
    }
    if (key === sent.current) return;
    const timer = window.setTimeout(() => {
      const ticket = guard.start();
      syncCart({
        lines: JSON.parse(key) as CartSyncLine[]
      }).then(() => {
        if (guard.isLatest(ticket)) sent.current = key;
      }).catch((err: unknown) => {
        if (guard.isLatest(ticket)) log.warn('winkelmand', 'Bewaren op de server lukte niet.', [err instanceof Error ? err.message : String(err)]);
      });
    }, DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [allowed, key]);

  // Weg (uitgelogd, andere app): niets wat nog loopt telt.
  useEffect(() => {
    const guard = requests.current;
    return () => guard.invalidate();
  }, []);
  return null;
}